import { useState } from "react";
import { useDropzone } from "react-dropzone";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { LABELS } from "~/resources/Labels";
import { useGetCloudProvisionerListWithPaginationQuery } from "~/services/cloudProvisioner";
import { VM_CPU_CORE_MAX, VM_STORAGE_MAX_GB } from "~/Utils/Consts";
import { SIMPLBreadcrumb } from "../../components/commons/breadcrumb/SimplBreadCrumb";
import { SIMPLButtonCustomized } from "../../components/commons/button/SIMPLButtonCustumized";
import { SIMPLMenuHeader } from "../../components/commons/header/SIMPLMenuHeader";
import { SIMPLCheckBox } from "../../components/commons/input/SIMPLCheckBox";
import SIMPLFile from "../../components/commons/input/SIMPLFile";
import { SIMPLInput } from "../../components/commons/input/SIMPLInput";
import { DualThumbSlider } from "../../components/commons/slider/SimplSliderDual";
import { LoadingWrapper } from "../../components/commons/spiner/SimpleLoaderWrapper";
import { UseFormCloudProvisioner } from "./UseFormCloudProvisioner";

export const AddCloudProvisioner = () => {
  const { refetch } = useGetCloudProvisionerListWithPaginationQuery({
    page: 0,
    size: 999,
  });
  const {
    handleSubmit,
    control,
    errors,
    submitForm,
    onIacDrop,
    isLoading,
    isError,
  } = UseFormCloudProvisioner(refetch);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: onIacDrop,
    maxFiles: 1,
    accept: {
      "text/plain": [".hcl", ".tf", ".tmpl"],
    },
  });
  const [isChecked, setIsChecked] = useState(false);
  const [cpuRange, setCpuRange] = useState({
    minValue: 1,
    maxValue: VM_CPU_CORE_MAX,
  });
  const [storageRange, setStorageRange] = useState({
    minValue: 5,
    maxValue: VM_STORAGE_MAX_GB,
  });
  const [ramRange, setRamRange] = useState({
    minValue: 1024,
    maxValue: 102400,
  });
  const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    handleSubmit((formValues) =>
      submitForm(formValues, isChecked, {
        minCpuCore: cpuRange.minValue,
        maxCpuCore: cpuRange.maxValue,
        minRam: ramRange.minValue,
        maxRam: ramRange.maxValue,
        minStorage: storageRange.minValue,
        maxStorage: storageRange.maxValue,
      }),
    )(event);
  };
  const handleCancel = () => {
    navigate("/cloud-provisioner-template");
  };

  return (
    <SIMPLMenuHeader>
      {/* PAGE CONTAINER */}
      <LoadingWrapper
        loadingTitle={t("LoadingCloudProvisioner")}
        isLoading={isLoading}
        isError={isError}
        testId="cloud-provisioner-loading"
      >
        <div className="flex justify-center bg-white px-6 py-8">
          {/* CARD */}
          <div className="w-full max-w-[1104px] rounded-xl border border-[#D6D6D6] bg-white">
            {/* HEADER: BREADCRUMB + TITLE */}
            <div className="px-8 pt-6">
              <SIMPLBreadcrumb />
              <h1 className="mt-2 font-['Arial'] text-[28px] font-bold leading-[44px] text-left text-Foundation/Heading normal-case">
                {t("AddCloudProvisioner")}
              </h1>
            </div>

            <form onSubmit={handleFormSubmit}>
              {/* CONTENT */}
              <div className="space-y-6 px-8 pb-8 pt-6">
                <div className="space-y-6 pt-2">
                  <SIMPLInput
                    name="fileName"
                    control={control}
                    rules={{
                      required: {
                        value: true,
                        message: LABELS.NameRequired,
                      },
                    }}
                    messageRequired={t("NameRequired")}
                    label={t("Name")}
                    placeholder={t("Name")}
                    required
                    id="fileName"
                    fontWeight="bold"
                    errorLabel={errors.fileName?.message}
                    testId="cloud-provisioner-fileName"
                  />
                </div>

                <div className="space-y-6 pt-2">
                  <Controller
                    name="file"
                    control={control}
                    rules={{ required: t("IacRequired") }}
                    render={({ field }) => (
                      <SIMPLFile
                        errors={errors}
                        errorFieldName="file"
                        currentScript={
                          field.value instanceof File ? field.value : null
                        }
                        onFileChange={field.onChange}
                        getRootProps={getRootProps}
                        getInputProps={getInputProps}
                        isDragActive={isDragActive}
                        LABELS={LABELS}
                        labelName={t("IacFile")}
                        dragDropFileHereLabel={t("DragDropFileHereOrClickHere")}
                        chooseFileLabel={t("ChooseFile")}
                        testId="cloud-provisioner-iac-file"
                        required
                      />
                    )}
                  />
                </div>

                <div className="space-y-6 pt-2">
                  <SIMPLInput
                    name="os"
                    control={control}
                    rules={{
                      required: {
                        value: true,
                        message: t("MessageOsRequired"),
                      },
                    }}
                    messageRequired={t("MessageOsRequired")}
                    label={t("Os")}
                    placeholder="ubuntu, debian, centos..."
                    required
                    id="os"
                    fontWeight="bold"
                    errorLabel={errors.os?.message}
                    testId="cloud-provisioner-os"
                  />
                </div>

                <div className="space-y-6 pt-2">
                  <SIMPLCheckBox
                    checked={isChecked}
                    onChange={(value) => setIsChecked(value)}
                    label={t("OVH")}
                    id=""
                    testId="cloud-provisioner-ovh"
                  />
                </div>

                {isChecked ? (
                  <>
                    <div className="grid gap-6 md:grid-cols-2 ">
                      <SIMPLInput
                        name="projectId"
                        control={control}
                        rules={{
                          required: {
                            value: true,
                            message: t("MessageProjectIdRequired"),
                          },
                        }}
                        messageRequired={t("MessageProjectIdRequired")}
                        label={t("ProjectID")}
                        placeholder={t("ProjectID")}
                        required
                        id="projectId"
                        fontWeight="bold"
                        errorLabel={errors.projectId?.message}
                        testId="cloud-provisioner-projectId"
                      />
                      <SIMPLInput
                        name="region"
                        control={control}
                        rules={{
                          required: {
                            value: true,
                            message: t("MessageRegionRequired"),
                          },
                        }}
                        messageRequired={t("MessageRegionRequired")}
                        label={t("Region")}
                        placeholder={t("Region")}
                        required
                        id="region"
                        fontWeight="bold"
                        errorLabel={errors.region?.message}
                        testId="cloud-provisioner-region"
                      />
                    </div>

                    <SIMPLInput
                      name="flavor"
                      control={control}
                      rules={{
                        required: {
                          value: true,
                          message: t("MessageFlavorRequired"),
                        },
                      }}
                      messageRequired={t("MessageFlavorRequired")}
                      label={t("Flavor")}
                      placeholder={t("Flavor")}
                      required
                      id="flavor"
                      fontWeight="bold"
                      errorLabel={errors.flavor?.message}
                      testId="cloud-provisioner-flavor"
                    />
                  </>
                ) : (
                  <>
                    {/*CPU*/}
                    <DualThumbSlider
                      id="cpuRange"
                      label={t("CPUCores")}
                      unity=""
                      min={1}
                      max={VM_CPU_CORE_MAX}
                      step={1}
                      minValue={cpuRange.minValue}
                      maxValue={cpuRange.maxValue}
                      onChange={(values) => setCpuRange(values)}
                      testId="cloud-provisioner-cpu-range"
                    />
                    {/*RAM*/}
                    <DualThumbSlider
                      id="ram"
                      label={t("RAM")}
                      unity="MB"
                      min={1024}
                      max={102400}
                      step={1}
                      minValue={ramRange.minValue}
                      maxValue={ramRange.maxValue}
                      onChange={(val: any) => setRamRange(val)}
                      testId="cloud-provisioner-ram"
                    />
                    {/*CPU*/}
                    <DualThumbSlider
                      id="storage"
                      label={t("Storage")}
                      unity="GB"
                      min={5}
                      max={VM_STORAGE_MAX_GB}
                      step={1}
                      minValue={storageRange.minValue}
                      maxValue={storageRange.maxValue}
                      onChange={(Storagevalues: any) =>
                        setStorageRange(Storagevalues)
                      }
                      testId="cloud-provisioner-storage"
                    />
                  </>
                )}
              </div>

              {/* ACTION BUTTONS – FULL WIDTH */}
              <div className="flex w-full justify-end gap-3 rounded-b-xl border-t border-[#D6D6D6] bg-[#F5F5F5] px-6 py-4">
                <SIMPLButtonCustomized
                  label={t("Cancel")}
                  secondary
                  buttonHandlerAction={handleCancel}
                  testId="cloud-provisioner-button-cancel"
                />
                <SIMPLButtonCustomized
                  label={t("Submit")}
                  primary
                  testId="cloud-provisioner-button-submit"
                />
              </div>
            </form>
          </div>
        </div>
      </LoadingWrapper>
    </SIMPLMenuHeader>
  );
};
